import type { ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * A plain semantic table for small, fixed datasets: entitlement lists, target
 * inventories, approval decisions. Query results use AG Grid instead, which
 * virtualises; this deliberately does not, because a real <table> is far
 * better for screen readers and prints cleanly into evidence packs.
 */

export interface Column<T> {
  readonly key: string;
  readonly header: ReactNode;
  readonly render: (row: T) => ReactNode;
  /** Right-aligns and applies tabular figures. */
  readonly numeric?: boolean;
  readonly className?: string;
  readonly headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  caption?: string;
  className?: string;
  /** Sticks the header while the container scrolls. */
  stickyHeader?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty,
  caption,
  className,
  stickyHeader = false,
}: DataTableProps<T>) {
  if (rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="min-w-full text-sm">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead
          className={cn('border-b border-line', stickyHeader && 'sticky top-0 bg-surface z-10')}
        >
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cn(
                  'px-3 py-2 text-left text-xs font-semibold text-subtle uppercase tracking-wider whitespace-nowrap',
                  c.numeric && 'text-right',
                  c.headerClassName,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn('transition-colors', onRowClick && 'cursor-pointer hover:bg-raised')}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'px-3 py-2 text-body align-top',
                    c.numeric && 'text-right tabular-nums',
                    c.className,
                  )}
                >
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Label/value pairs for detail views. */
export function DescriptionList({
  items,
  className,
}: {
  items: readonly { label: ReactNode; value: ReactNode }[];
  className?: string;
}) {
  return (
    <dl className={cn('grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3', className)}>
      {items.map((item, i) => (
        <div key={i} className="min-w-0">
          <dt className="text-xs font-medium text-subtle uppercase tracking-wider">{item.label}</dt>
          <dd className="text-sm text-body mt-0.5 break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
